import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { TalkingPointsDefinition } from '@r1-compass-library/models';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';

export class PlayImporterLookupFieldMappingForm {
    fileHeaderName: FormControl<string> = new FormControl<string>('', {
        validators: Validators.required,
        nonNullable: true,
    });
    lookupFieldName: FormControl<string> = new FormControl<string>('', {
        validators: Validators.required,
        nonNullable: true,
    });

    constructor(lookupFieldName: string, fileHeaderName: string) {
        this.lookupFieldName.setValue(lookupFieldName);
        this.lookupFieldName.disable();
        this.fileHeaderName.setValue(fileHeaderName);
    }
}

export function initForm(
    form: FormGroup<PlayTalkingPointsForm>,
    talkingPtsDef?: TalkingPointsDefinition,
    fileHeaders?: string[]
) {
    form.controls.froalaContentFormControl.patchValue(talkingPtsDef?.talkingPointsTemplate ?? '');
    form.controls.playImporterLookupFieldMappings.clear();

    if (talkingPtsDef?.playImporterLookupFieldMappings) {
        talkingPtsDef.playImporterLookupFieldMappings.forEach(mapping => {
            const normalizedFileHeaders = fileHeaders?.map(header => header.trim()) ?? [];
            const fileHeaderToMap = normalizedFileHeaders.find(header => header === mapping.fileHeaderName) ?? '';
            form.controls.playImporterLookupFieldMappings.push(
                new FormGroup<PlayImporterLookupFieldMappingForm>(
                    new PlayImporterLookupFieldMappingForm(mapping.lookupFieldName, fileHeaderToMap)
                )
            );
        });
    }
    form.markAllAsTouched();
}

export class PlayTalkingPointsForm {
    froalaContentFormControl = new FormControl('', { validators: [Validators.required], nonNullable: true });
    playImporterLookupFieldMappings = new FormArray<FormGroup<PlayImporterLookupFieldMappingForm>>([]);
}

@Injectable()
export class PlayTalkingPointsFormService {
    readonly #fb = inject(NonNullableFormBuilder);
    readonly #formSaveWarningStore = inject(SaveGuardStore);

    public form: FormGroup<PlayTalkingPointsForm> = this.#fb.group(new PlayTalkingPointsForm());

    public canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.form);
    public saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningEntityDefinition;

    public initForm(talkingPtsDef?: TalkingPointsDefinition, fileHeaders?: string[]) {
        initForm(this.form, talkingPtsDef, fileHeaders);
    }

    /**
     * Ensures that the play-importer lookup field form array has all the necessary mappings and nothing extra.
     *
     * For any existing mappings, the mapping will be removed if the mapped lookup field name is no longer being referenced (ie: the
     * talking pts has no field merges for that lookup field)
     *
     * For any referenced datamart lookup field name, this method will create a mapping if one doesn't exist. In addition, if the lookup field
     * name exists as a header of the import file (case insensitive), then that header is automatically selected as a target.
     *
     **/
    public trueUpLookupFieldMappings(referencedLookupFieldNames: Set<string>, fileHeaders: string[]) {
        const normalizedFileHeaders = fileHeaders.map(header => header.toLowerCase().trim());
        const lookupFieldMappings = this.form.controls.playImporterLookupFieldMappings;

        // If a lookup field is mapped, but not referenced by existing field merge, then remove it.
        for (let i = lookupFieldMappings.length - 1; i >= 0; i--) {
            const lookupFieldName = lookupFieldMappings.at(i).controls.lookupFieldName.value;
            if (!referencedLookupFieldNames.has(lookupFieldName)) {
                lookupFieldMappings.removeAt(i, { emitEvent: true });
                this.form.markAllAsDirty({ emitEvent: true });
            }
        }

        // If there are any referenced lookup field names that are not already mapped, then create a mapping for
        // that lookup field name (and if that name exists as a file header, map it to that header)
        const mappedLookupFieldNames = new Set(
            lookupFieldMappings.getRawValue().map(mapping => mapping.lookupFieldName)
        );
        const unmappedLookupFieldNames = referencedLookupFieldNames.difference(mappedLookupFieldNames);
        if (unmappedLookupFieldNames.size > 0) {
            unmappedLookupFieldNames.forEach(fieldName => {
                const newMapping = new FormGroup<PlayImporterLookupFieldMappingForm>(
                    new PlayImporterLookupFieldMappingForm(fieldName, '')
                );
                if (normalizedFileHeaders.includes(fieldName.toLowerCase().trim())) {
                    newMapping.controls.fileHeaderName.setValue(fieldName);
                }
                this.form.controls.playImporterLookupFieldMappings.push(newMapping, { emitEvent: true });
                this.form.markAllAsDirty({ emitEvent: true });
            });
        }
    }
}
